import * as Icons from '@ant-design/icons';
import { BaseKey, useCan, useList } from '@refinedev/core';
import {
  Button,
  DatePicker,
  Divider,
  Pagination,
  Radio,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography
} from 'antd';
import { ClassRecordingsModal } from 'components/modal/professor_area/classroom/ClassRecordingsModal';
import { CreateClassModal } from 'components/modal/professor_area/classroom/CreateClassModal';
import { Dayjs } from 'dayjs';
import { EmptyTab } from '../EmptyTab';
import { IInteractiveClass } from 'interfaces/interactive_classes';
import { IProfessorAreaClassroom } from 'interfaces/professor_area_classrooms';
import { ShowClassModal } from 'components/modal/professor_area/classroom/ShowClassModal';
import { TableRowItem } from './TableRowItem';
import { getUserId } from 'services/auth';
import { toRangeDateFilter } from 'services/date';
import { useCallback, useMemo, useState } from 'react';
import { useCanMultiple } from 'components/layout/sider';
import { useModalForm } from '@refinedev/antd';

export interface TabInteractiveClassesProps {
  classroom: IProfessorAreaClassroom;
}

const NEXT_CLASS_STATUS = ['created', 'started'];
const FINISHED_CLASS_STATUS = ['finished'];
const PAGE_SIZE = 10;

export const TabInteractiveClasses = ({
  classroom
}: TabInteractiveClassesProps) => {
  const [editModal, setEditModal] = useState(false);
  const [recordingsForClass, setRecordingsForClass] =
    useState<IInteractiveClass | null>(null);
  const [filter, setFilter] = useState('progress');
  const [filterMyClasses, setFilterMyClasses] = useState(false);
  const [period, setPeriod] = useState<[Dayjs, Dayjs] | null>(null);
  const [current, setCurrent] = useState(1);

  const { data: createPermission } = useCan({
    resource: 'professor_area/interactive_classes',
    action: 'create'
  });
  const canCreateInteractiveClass = createPermission?.can ?? false;
  const [showPermission, editPermission, deletePermission] = useCanMultiple([
    { resource: 'professor_area/interactive_classes', action: 'show' },
    { resource: 'professor_area/interactive_classes', action: 'edit' },
    { resource: 'professor_area/interactive_classes', action: 'delete' }
  ]);
  const canShowInteractiveClass = showPermission.data?.can ?? false;
  const canEditInteractiveClass = editPermission.data?.can ?? false;
  const canDeleteInteractiveClass = deletePermission.data?.can ?? false;
  const isFinishedFilter = filter === 'finished';
  const sectionTitle = isFinishedFilter ? 'Aulas realizadas' : 'Próximas aulas';

  const { data, isLoading, refetch, isFetching } = useList({
    resource: 'professor_area/interactive_classes',
    pagination: {
      current,
      pageSize: PAGE_SIZE,
      mode: 'server'
    },
    queryOptions: {
      keepPreviousData: true
    },
    filters: [
      {
        field: 'filter[by_classroom_id]',
        operator: 'eq',
        value: classroom.id
      },
      {
        field: 'filter[by_agent_id]',
        operator: 'eq',
        value: filterMyClasses ? getUserId() : undefined
      },
      {
        field: 'filter[by_status][]',
        operator: 'eq',
        value: filter === 'finished' ? FINISHED_CLASS_STATUS : NEXT_CLASS_STATUS
      },
      {
        field: 'filter[by_range_date][]',
        operator: 'eq',
        value: toRangeDateFilter(period)
      }
    ]
  });

  const interactiveClasses = data?.data as IInteractiveClass[];

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createModalShow
  } = useModalForm<IInteractiveClass>({
    resource: 'professor_area/interactive_classes',
    action: 'create',
    redirect: false,
    successNotification: () => ({
      type: 'success',
      message: 'Aula interativa criada com sucesso.'
    })
  });

  const {
    modalProps: showModalProps,
    formProps: showFormProps,
    show: showModalShow,
    close: closeModalShow
  } = useModalForm<IInteractiveClass>({
    resource: 'professor_area/interactive_classes',
    action: 'edit',
    redirect: false,
    warnWhenUnsavedChanges: false
  });

  const openRecordingsModal = useCallback(
    (interactiveClass: IInteractiveClass) => {
      setRecordingsForClass(interactiveClass);
    },
    []
  );

  const closeRecordingsModal = useCallback(() => {
    setRecordingsForClass(null);
  }, []);

  const handleShowOrEdit = useCallback(
    (isEdit: boolean, id?: BaseKey) => () => {
      setEditModal(isEdit);
      showModalShow(id);
    },
    [showModalShow]
  );

  const hasData = useMemo(() => {
    return !isLoading && interactiveClasses && interactiveClasses.length > 0;
  }, [isLoading, interactiveClasses]);

  if (isLoading) {
    return (
      <Skeleton active paragraph={{ rows: 6 }} className="w-full h-full" />
    );
  }

  const EmptyComponent = () => {
    return (
      <EmptyTab
        title="Nenhuma aula interativa encontrada"
        buttonText={
          canCreateInteractiveClass && !isFinishedFilter
            ? 'Nova aula'
            : undefined
        }
        onClick={
          canCreateInteractiveClass && !isFinishedFilter
            ? () => createModalShow()
            : undefined
        }
      />
    );
  };

  const ContentComponent = () => {
    return (
      <>
        <Space direction="vertical" className="w-full !gap-8">
          <Space className="flex w-full p-2 rounded-md bg-gray-50">
            <Radio.Group
              buttonStyle="solid"
              value={filter}
              onChange={e => {
                setFilter(e.target.value);
                setCurrent(1);
              }}
            >
              <Radio.Button value="progress">Próximas aulas</Radio.Button>
              <Radio.Button value="finished">Aulas realizadas</Radio.Button>
            </Radio.Group>
            <Switch
              value={filterMyClasses}
              defaultValue={filterMyClasses}
              onChange={() => {
                setFilterMyClasses(!filterMyClasses);
                setCurrent(1);
              }}
              className="!ml-1"
            />
            <Typography.Paragraph className="!m-0">
              Minhas aulas
            </Typography.Paragraph>
            <DatePicker.RangePicker
              value={period}
              onChange={dates => {
                setPeriod(dates?.[0] && dates[1] ? [dates[0], dates[1]] : null);
                setCurrent(1);
              }}
              placeholder={['Data inicial', 'Data final']}
              format="DD/MM/YYYY"
              className="!ml-4"
            />
          </Space>

          <Space className="flex w-full justify-between">
            <Space size="middle">
              <Typography.Title level={5} className="!m-0">
                {sectionTitle}
              </Typography.Title>
              <Tag>{data?.total ?? undefined}</Tag>
            </Space>

            {canCreateInteractiveClass && (
              <Button
                type="primary"
                icon={<Icons.PlusOutlined />}
                onClick={() => createModalShow()}
              >
                Nova aula
              </Button>
            )}
          </Space>
        </Space>

        <Divider className="w-full" />

        {!isLoading && hasData ? (
          interactiveClasses.map((interactiveClass, index) => (
            <TableRowItem
              key={index.toString()}
              showModal={handleShowOrEdit}
              onWatchRecordings={openRecordingsModal}
              interactiveClass={interactiveClass}
              refetch={refetch}
              canShow={canShowInteractiveClass}
              canEdit={canEditInteractiveClass}
              canDelete={canDeleteInteractiveClass}
            />
          ))
        ) : (
          <>
            {isFetching ? (
              <Skeleton
                active
                paragraph={{ rows: 6 }}
                className="w-full h-full"
              />
            ) : (
              <EmptyComponent />
            )}
          </>
        )}

        {hasData && (
          <Pagination
            className="justify-self-end mt-4 text-center"
            hideOnSinglePage
            total={data?.total}
            pageSize={PAGE_SIZE}
            current={current}
            onChange={setCurrent}
          />
        )}
      </>
    );
  };

  return (
    <>
      <ContentComponent />

      {createModalProps.open && (
        <CreateClassModal
          modalProps={createModalProps}
          formProps={createFormProps}
        />
      )}

      {showModalProps.open && (
        <ShowClassModal
          isEdit={editModal}
          modalProps={showModalProps}
          formProps={showFormProps}
          closeModal={closeModalShow}
          onWatchRecordings={openRecordingsModal}
          refetch={refetch}
          canEdit={canEditInteractiveClass}
          canDelete={canDeleteInteractiveClass}
        />
      )}

      <ClassRecordingsModal
        interactiveClass={recordingsForClass}
        open={Boolean(recordingsForClass)}
        onClose={closeRecordingsModal}
      />
    </>
  );
};
